import React, { useState, useRef, useEffect } from "react";
import "./Auth.css";
import loginVec from "../../Assets/Images/resetVec.webp";
import brandLogo from "../../Assets/Images/brandLogo.png";
import { useLocation, useNavigate } from "react-router-dom";
import { postApiCaller } from "../../Libs/apiCaller";
import { toast } from "react-toastify";
import { useCookies } from "react-cookie";

const OtpVerify = () => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(120);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = useRef([]);
  const location = useLocation();
  const navigate = useNavigate();
  const [, setCookie] = useCookies(["accessToken"]);

  useEffect(() => {
    let interval;

    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Allow only numbers

    let newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    } else if (value && index === 5) {
      inputRefs.current[5].focus();
      setError("");
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pasteData)) {
      setOtp(pasteData.split(""));
      inputRefs.current[5].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.join("").length < 6) {
      setError("Please enter a valid 6-digit OTP.");
    } else {
      setIsLoading(true);
      const endPoint = "auth/verify_forgot_password_otp";
      const payload = {
        phone: location.state.phone,
        otp: Number(otp.join("")),
      };

      const response = await postApiCaller(endPoint, payload);
      if (response?.status === 200) {
        setError("");
        localStorage.setItem("accessToken", response.data.accessToken);
        navigate("/reset-password", {
          state: {
            phone: location.state.phone,
          },
          replace: true // This prevents going back to OTP page
        });
      } else {
        setError(response?.response?.data?.message);
      }
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    // Trigger your resend OTP API here
    const endPoint = "auth/send_otp";
    const payload = {
      phone: location.state.phone,
      countryCode: location.state.countryCode,
    };
    const response = await postApiCaller(endPoint, payload);
    if (response?.status === 200) {
      toast.success("OTP sent successfully");
      setError("");
      setOtp(["", "", "", "", "", ""]);
      setTimer(110);
    } else {
      setError(response?.response?.data?.message);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <section
      className="bg-login p-6 md:p-4 pb-16 sm:pb-0 min-h-screen flex items-center justify-center
overflow-x-hidden"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 items-stretch w-full max-w-8xl mx-auto gap-0 md:gap-10">
        {/* Right Section (Image & Text) */}
        <div
          className="hidden md:grid relative grid-cols-1 h-full min-h-[calc(100vh-40px)] bg-cover bg-center rounded-xl"
          style={{ backgroundImage: `url(${loginVec})` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-20 rounded-xl"></div>

          <div className="absolute top-0 text-left text-white p-8 w-3/4">
            <img src={brandLogo} alt="Brand Logo" className="w-20 h-20 mb-4 drop-shadow-lg" />
          </div>
          <div className="">
            <div className="absolute bottom-4 mx-4 w-[calc(100%-2rem)] text-left rounded-lg text-white p-3 backdrop-blur-xs border border-white/30 bg-white/10">
              <p className="capitalize text-md">
                We believe communication should be simple, fast and private.
                Whether you're chatting with friends, connecting with family or
                collaborating with your team, our platform ensures seamless
                messaging and end-to-end security.
              </p>
            </div>
          </div>
        </div>

        {/* Left Section (Form) */}
        <div className="flex items-center justify-center px-4 md:px-0">
          <div className="w-full max-w-lg ">
            <h2 className="text-3xl leading-[50px] font-bold text-gray-900 w-3/2 mb-4 capitalize">
              Password reset code
            </h2>

            <p className="text-md text-gray-500 mb-6 ">
              We’ve sent an SMS with an activation code to <br /> your phone{" "}
              <b>
                {" "}
                {location.state.countryCode} {location.state.phone}{" "}
              </b>
            </p>

            {/* OTP Input Fields */}
            <form onSubmit={handleSubmit}>
              <div
                className={`flex justify-between gap-2 ${
                  error ? "mb-2" : "mb-7"
                }`}
              >
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    value={digit}
                    maxLength="1"
                    className="w-[72px] h-[60px] text-center text-lg font-semibold bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-600"
                    ref={(el) => (inputRefs.current[index] = el)}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                  />
                ))}
              </div>
              {error && <p className="text-red-500 text-sm mb-7">{error}</p>}

              <button
                type="submit"
                className="w-full bg-[var(--primary-color)] text-white py-3 rounded-full text-lg font-medium hover:bg-purple-500 transition"
                disabled={isLoading}
              >
                {isLoading ? "Verifying..." : "Verify OTP"}
              </button>
            </form>

            {/* Resend Code */}
            <div className="flex justify-center mt-8 items-center space-x-1">
              <p className="text-black text-md">Didn't receive code?</p>

              {timer > 0 ? (
                <p className="text-blue-600 font-normal">{formatTime(timer)}</p>
              ) : (
                <button
                  className="text-blue-600 text-md hover:underline cursor-pointer"
                  onClick={handleResend}
                >
                  Resend
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OtpVerify;
